/*
  QRCodeInvite.js – Handles guest registration via QR code invite links.

=============== PURPOSE =================
  This component allows both host- and guest-generated QR codes to serve as
  entry points for new guest sign-ups to an event on the Speakeasy platform.

=============== KEY BEHAVIORS ================
  - Parses the QR token from the URL (e.g., /invite/host_abc123 or /invite/xyz456)
  - If the token starts with "host_", it fetches the event by matching host_qr_token
  - If it's a guest token, it fetches the inviter's guest record, then uses their event_id
  - Displays a form for prospective guests to enter:
      • First/last name
      • Email address
      • Passphrase (used later to access the event)
  - On submission:
      • Creates a new guest row in Supabase `guests` table
      • Associates the guest with the correct `event_id`
      • Inherits the event’s VRC code
      • If invited by another guest, sets `invited_by` to the inviter’s guest ID
  - Displays loading states, form validation errors, and a success message

=================== NOTES ====================
  - All guest sign-ups are tied to a specific event via `event_id`
  - QR codes are assumed to be long/random enough to act as secure access points
  - Passphrases are used later in Speakeasy.js to authenticate access
  - Designed to support a viral/chain invitation model (guests inviting more guests)

*/

import { sendInvite } from '../utils/sendInvite';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router-dom';
import supabase from '../config/supabaseClient';
import '../components/qrcodeinvite.css'; // ✅ Correct path and file name

export default function QRCodeInvite() {
  const { qrToken } = useParams();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // 🔄 Load event info based on eventId in URL
  useEffect(() => {
    const fetchEvent = async () => {
      if (!qrToken) return;

      console.log("🔍 Full QR token:", qrToken);
      console.log("🧩 Final token used in query:", qrToken.replace('host_', ''));

      // Host QR
      if (qrToken.startsWith('host_')) {
        const { data, error } = await supabase
          .from('hosting')
          .select('id, event_name, event_image, vrc')
          .eq('host_qr_token', qrToken.replace('host_', ''))
          .single();

        if (error) {
          console.error('Error fetching host event:', error);
          setError('Event not found.');
        } else {
          setEvent(data);
        }
      }

      // Guest QR
      else {
        const { data: guestInfo, error: guestError } = await supabase
          .from('guests')
          .select('event_id')
          .eq('guest_qr_token', qrToken)
          .single();

        if (guestError || !guestInfo) {
          console.error('Invalid QR token:', guestError);
          setError('Invalid invite.');
          return;
        }

        const { data: eventData, error: eventErr } = await supabase
          .from('hosting')
          .select('id, event_name, event_image, vrc')
          .eq('id', guestInfo.event_id)
          .single();

        if (eventErr) {
          console.error('Could not load event:', eventErr);
          setError('Event not found.');
        } else {
          setEvent(eventData);
        }
      }

      setLoading(false);
    };

    fetchEvent();
  }, [qrToken]);


  // 📨 Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !passphrase || (!firstName && !lastName)) {
      setError('Please enter a valid email, passphrase, and at least a first or last name.');
      return;
    }

    try {
      // Step 1: Check for existing guest with same email + event
      const { data: existingGuest } = await supabase
        .from('guests')
        .select('id')
        .eq('guest_email', email)
        .eq('event_id', event.id)
        .maybeSingle();

      if (existingGuest) {
        setError('You’ve already signed up for this event!');
        return;
      }

      // Step 2: Build the guest object
      const newGuest = {
        event_id: event.id,
        guest_first_name: firstName || null,
        guest_last_name: lastName || null,
        guest_email: email,
        passphrase: passphrase,
        vrc: event.vrc,
        status: 'invited',
        guest_qr_token: uuidv4(),
      };

      // Step 3: If invited by another guest, add inviter's ID
      if (qrToken && !qrToken.startsWith('host_')) {
        const { data: inviter, error: inviterError } = await supabase
          .from('guests')
          .select('id')
          .eq('guest_qr_token', qrToken)
          .single();

        if (inviter && inviter.id) {
          newGuest.invited_by = inviter.id;
        }
      }

      // Step 4: Insert guest
      const { error } = await supabase.from('guests').insert([newGuest]);

      if (error) {
        console.error('Supabase insert error:', error);
        setError('Something went wrong while saving your info. Please try again.');
      } else {
        setSubmitted(true);

        // 📤 Send confirmation email
        try {
          await sendInvite({
            to: email,
            name: firstName || lastName || "Guest",
            eventTitle: event.event_name,
            passphrase,
            vrc: event.vrc,
            moderatorContact: "@host", // Optional: Replace with real host info if you have it
            link: `${window.location.origin}/enter/${event.id}`, // or whatever entry link makes sense
          });
          console.log("✅ Invite email sent!");
        } catch (emailErr) {
          console.error("📪 Failed to send email:", emailErr.message);
          // optionally: notify the user or silently fail if email is not critical
        }

      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Unexpected error occurred.');
    }


  };

  if (loading) return <div className="qrcodeinvite-loading">Loading...</div>;
  if (error) return <div className="qrcodeinvite-error">{error}</div>;
  if (!event) return <div className="qrcodeinvite-error">No event found.</div>;

  return (
    <div className="qrcodeinvite-container">
      <h1 className="qrcodeinvite-title">You’re Invited to: {event.event_name}!</h1>
      <img src={event.event_image} alt="Event" className="qrcodeinvite-image" />

      {!submitted ? (
        <form className="qrcodeinvite-form" onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <input
            type="text"
            placeholder="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          <input
            type="email"
            placeholder="Email Address *"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="text"
            placeholder="Choose a Passphrase"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            required
          />
          <small style={{ fontSize: '0.8rem', marginTop: '-8px', marginBottom: '10px', display: 'block' }}>
            You'll need this to access the event later. Pick something fun or memorable!
          </small>

          {error && <p className="qrcodeinvite-error">{error}</p>}
          <button type="submit">Send Me the Invite</button>
        </form>
      ) : (
        <div className="qrcodeinvite-success">
          <p>🎉 Thanks! We'll email you the details shortly.</p>
        </div>
      )}
    </div>
  );
}
